import { Controller } from "@hotwired/stimulus";

/**
 * BestInPlace Controller para Stimulus
 *
 * Implementação autocontida que converte spans .best_in_place em campos editáveis com AJAX
 * Não depende de jQuery ou do plugin best_in_place
 */
export default class extends Controller {
  static values = {
    options: Object,
  };

  connect() {
    this.activate();
    this.observeTurboEvents();
  }

  /**
   * Observa eventos Turbo para re-ativar após navegação
   */
  observeTurboEvents() {
    document.addEventListener("turbo:load", () => this.activate());
    document.addEventListener("turbo:render", () => this.activate());
  }

  /**
   * Ativa edição inline em todos os elementos .best_in_place dentro do controller
   */
  activate() {
    const elements = this.element.querySelectorAll(".best_in_place");
    if (elements.length === 0) return;

    elements.forEach((element) => {
      if (element.dataset.activated) return; // Evitar ativar múltiplas vezes
      element.dataset.activated = "true";
      this.makeEditable(element);
    });
  }

  /**
   * Torna um elemento editável
   */
  makeEditable(span) {
    // Extrair atributos do span
    const originalValue = span.textContent.trim();
    const fieldName = span.dataset.attribute;
    const resourceName = span.dataset.objectId; // camelCase pq dataset converte kebab-case
    const editType = span.dataset.type || span.dataset.as || "input"; // Suportar data-as ou data-type
    const url = this.getUpdateUrl(span);

    // Adicionar classe para styling
    span.classList.add("best-in-place-clickable");
    span.style.cursor = "pointer";

    // Adicionar listener de clique
    span.addEventListener("click", (e) => {
      e.stopPropagation();
      if (span.querySelector("input, textarea, select")) return; // Já em modo edição
      this.openEditor(span, originalValue, fieldName, editType, url);
    });
  }

  /**
   * Abre o editor inline
   */
  openEditor(span, originalValue, fieldName, editType, url) {
    // Limpar conteúdo do span
    span.innerHTML = "";

    let input;

    if (editType === "textarea") {
      input = document.createElement("textarea");
      input.value = originalValue;
      input.className = "form-control form-control-sm";
      input.rows = 3;
    } else if (editType === "select") {
      input = document.createElement("select");
      input.className = "form-control form-control-sm";
      input.innerHTML = span.dataset.options || "";
      input.value = originalValue;
    } else {
      input = document.createElement("input");
      input.type = span.dataset.inputType || "text";
      input.value = originalValue;
      input.className = "form-control form-control-sm";
      if (span.dataset.htmlAttrs) {
        try {
          const attrs = JSON.parse(span.dataset.htmlAttrs);
          Object.entries(attrs).forEach(([key, value]) => {
            input.setAttribute(key, value);
          });
        } catch (e) {
          // Ignorar erro ao parse
        }
      }
    }

    input.style.marginRight = "5px";
    span.appendChild(input);

    // Botões OK e Cancel
    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.className = span.dataset.okButtonClass || "btn btn-sm btn-primary";
    okBtn.textContent = span.dataset.okButton || "OK";
    okBtn.style.marginRight = "3px";

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className =
      span.dataset.cancelButtonClass || "btn btn-sm btn-secondary";
    cancelBtn.textContent = span.dataset.cancelButton || "Cancel";

    span.appendChild(okBtn);
    span.appendChild(cancelBtn);

    // Focus no input
    input.focus();
    input.select?.();

    let ignoreBlurSave = false;
    let actionInProgress = false;

    // Handler para OK
    const handleSave = () => {
      if (actionInProgress) return;
      actionInProgress = true;
      this.saveChanges(span, input, fieldName, input.value, url, originalValue);
    };

    // Handler para Cancel
    const handleCancel = () => {
      if (actionInProgress) return;
      actionInProgress = true;
      ignoreBlurSave = true;
      this.closeEditor(span, originalValue);
    };

    // Marca intenção antes do blur acontecer ao clicar nos botões
    okBtn.addEventListener("mousedown", (e) => {
      e.stopPropagation();
      ignoreBlurSave = true;
    });
    cancelBtn.addEventListener("mousedown", (e) => {
      e.stopPropagation();
      ignoreBlurSave = true;
    });

    okBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleSave();
    });
    cancelBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleCancel();
    });

    // ESC para cancelar
    input.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        handleCancel();
      } else if (e.key === "Enter" && editType !== "textarea") {
        handleSave();
      }
    });

    // Blur deve cancelar (mesmo comportamento do botão Cancelar)
    if (editType !== "textarea") {
      input.addEventListener("blur", () => {
        if (ignoreBlurSave || actionInProgress) return;
        if (input.parentNode) handleCancel();
      });
    }
  }

  /**
   * Salva as mudanças via AJAX
   */
  async saveChanges(span, input, fieldName, newValue, url, originalValue) {
    try {
      const payload = this.buildPayload(span, fieldName, newValue);

      // Usar fetch com headers corretos para Rails
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await this.parseJsonIfPresent(response);
        this.notifySuccess(`Campo atualizado com sucesso`);
        this.closeEditor(span, newValue);
        this.dispatchUpdateEvent(span, fieldName);
      } else {
        let errorMsg = "Erro ao atualizar campo";
        try {
          const errors = await response.json();
          errorMsg = Array.isArray(errors)
            ? errors.join(", ")
            : errors.message || errorMsg;
        } catch (e) {
          errorMsg = `Erro ${response.status}: ${response.statusText}`;
        }
        this.notifyError(errorMsg);
        this.closeEditor(span, originalValue);
      }
    } catch (error) {
      console.error("BestInPlace: Erro ao salvar", error);
      this.notifyError(`Erro: ${error.message}`);
      this.closeEditor(span, originalValue);
    }
  }

  /**
   * Extrair CSRF token do meta tag
   */
  getCsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]');
    return token ? token.getAttribute("content") : "";
  }

  async parseJsonIfPresent(response) {
    const contentType = response.headers.get("content-type") || "";
    const contentLength = response.headers.get("content-length");

    if (!contentType.includes("application/json")) return null;
    if (contentLength === "0") return null;

    const text = await response.text();
    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch (_error) {
      return null;
    }
  }

  /**
   * Construir payload de acordo com a estrutura esperada
   */
  buildPayload(span, fieldName, value) {
    const resourceId = span.dataset.objectId || "resource"; // Ex: "store-schedule-123"
    const payload = {};

    // Suportar nested routes: "store-schedule-123" -> { schedule: { spots: value } }
    if (resourceId.includes("-")) {
      const parts = resourceId.split("-");
      // Se tem mais de 2 partes, é nested. Ex: ["store", "schedule", "123"]
      // Queremos o penúltimo elemento (o tipo de recurso)
      const resourceType = parts[parts.length - 2] || parts[0];
      payload[resourceType] = { [fieldName]: value };
    } else {
      payload[resourceId] = { [fieldName]: value };
    }

    return payload;
  }

  /**
   * Fecha o editor e mostra o valor
   */
  closeEditor(span, value) {
    delete span.dataset.activated; // Permitir reativação
    span.innerHTML = this.escapeHtml(value) || span.dataset.placeholder || "-";
    span.style.cursor = "pointer";
    span.classList.add("best-in-place-clickable");

    // Re-ativar para próxima edição
    setTimeout(() => this.makeEditable(span), 100);
  }

  /**
   * Obter URL de atualização
   */
  getUpdateUrl(span) {
    // Se o span tem data-url, usar
    if (span.dataset.url) return span.dataset.url;

    // Tentar extrair da data-object-id (ex: store-schedule-1 -> /store/schedules/1)
    const objectId = span.dataset.objectId;
    if (objectId) {
      const parts = objectId.split("-");
      if (parts.length >= 2) {
        const resource = parts[0];
        const id = parts[parts.length - 1];
        return `/${resource}s/${id}`;
      }
    }

    // Fallback para URL atual
    return window.location.pathname;
  }

  /**
   * Notificar sucesso
   */
  notifySuccess(message) {
    if (window.Swal) {
      window.Swal.fire({
        icon: "success",
        title: "Sucesso",
        text: message,
        timer: 2000,
      });
    } else if (window.PNotify) {
      new window.PNotify({
        title: "Sucesso",
        text: message,
        type: "success",
      });
    }
  }

  /**
   * Notificar erro
   */
  notifyError(message) {
    if (window.Swal) {
      window.Swal.fire({
        icon: "error",
        title: "Erro",
        text: message,
        timer: 3000,
      });
    } else if (window.PNotify) {
      new window.PNotify({
        title: "Erro",
        text: message,
        type: "error",
      });
    } else {
      alert(`Erro: ${message}`);
    }
  }

  /**
   * Dispatch evento customizado
   */
  dispatchUpdateEvent(span, fieldName) {
    span.dispatchEvent(
      new CustomEvent("best-in-place:updated", {
        detail: { element: span, field: fieldName },
        bubbles: true,
      }),
    );
  }

  /**
   * Escapar HTML
   */
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
